'use client';

import { useState, Fragment } from 'react';
import { useExtensionAuth } from 'src/hooks/useExtensionAuth';
import { ExclamationTriangleIcon, XMarkIcon, CursorArrowRaysIcon, RocketLaunchIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

export default function ExtensionBanner() {
  const { extensionStatus } = useExtensionAuth();
  const [showDetails, setShowDetails] = useState(false);

  // Don't show banner while checking or if there's an error
  if (extensionStatus.isChecking || extensionStatus.error || !extensionStatus.isInstalled) {
    return null;
  }

  // Subtle indicator - small badge/pill that shows extension is connected
  return (
    <>
      <button
        onClick={() => setShowDetails(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full text-xs hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer"
        title="Click for extension details"
      >
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-green-700 font-medium">Extension Active</span>
      </button>

      {/* Extension Details Modal */}
      <Transition appear show={showDetails} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDetails(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-gradient-to-br from-white to-gray-50 text-left align-middle shadow-2xl transition-all border border-gray-100">
                  {/* Header with gradient background */}
                  <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5">
                    <Dialog.Title className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <RocketLaunchIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            Extension Connected!
                          </h3>
                          <p className="text-green-100 text-sm">Ready to verify facts anywhere</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowDetails(false)}
                        className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </Dialog.Title>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Status Card */}
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 p-4 border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <ShieldCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-800">Synced & Secure</p>
                          <p className="text-sm text-green-600">Extension authenticated with your account</p>
                        </div>
                      </div>
                      {/* Animated background decoration */}
                      <div className="absolute -right-4 -top-4 w-16 h-16 bg-green-200/30 rounded-full"></div>
                      <div className="absolute -right-2 -bottom-2 w-12 h-12 bg-emerald-200/40 rounded-full"></div>
                    </div>

                    {/* How to Use - Visual Steps */}
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CursorArrowRaysIcon className="h-5 w-5 text-blue-500" />
                        How to verify facts:
                      </h4>
                      <div className="space-y-3">
                        {[
                          { step: "1", action: "Select text on any webpage", icon: "🎯", color: "blue" },
                          { step: "2", action: "Right-click → 'Verify with Mira'", icon: "🖱️", color: "purple" },
                          { step: "3", action: "Get instant AI consensus results", icon: "⚡", color: "green" }
                        ].map((item) => (
                          <div key={item.step} className="flex items-center gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="flex-shrink-0">
                              <div className={`w-8 h-8 rounded-full bg-${item.color}-100 flex items-center justify-center text-lg`}>
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold px-2 py-1 rounded-full bg-${item.color}-100 text-${item.color}-800`}>
                                  STEP {item.step}
                                </span>
                                <span className="text-sm font-medium text-gray-900">{item.action}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pro Tips */}
                    <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 p-4 border border-amber-200">
                      <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                        💡 Pro Tips
                      </h4>
                      <ul className="space-y-1 text-sm text-amber-700">
                        <li>• Perfect for news articles, social media posts, and research</li>
                        <li>• Works instantly on any website - no page reload needed</li>
                        <li>• AI consensus from multiple models ensures accuracy</li>
                      </ul>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center pt-2">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-sm font-medium shadow-lg">
                        <span>Ready to verify facts!</span>
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export function ExtensionInstallPrompt() {
  const { extensionStatus } = useExtensionAuth();

  // Show install prompt if extension is not installed
  if (extensionStatus.isChecking || extensionStatus.isInstalled) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-start">
        <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-blue-800 mb-1">
            Get the Chrome Extension
          </h3>
          <p className="text-sm text-blue-700 mb-3">
            Download and install our Chrome extension to verify facts directly on any webpage without switching tabs. 
            After downloading, enable &quot;Developer mode&quot; in Chrome extensions and load the unpacked folder.
          </p>
          <button
            onClick={() => {
              // Open GitHub releases page for latest extension download
              window.open('https://github.com/Aroha-Labs/mira-verification-demo/releases/latest', '_blank');
            }}
            className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Download Extension
          </button>
        </div>
      </div>
    </div>
  );
}